import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { FlexLayoutModule } from '@angular/flex-layout';

import { UiAlertModule } from '@cigna/shared/angular/alert-ui';
import { UiIconModule } from '@cigna/shared/angular/icon-ui';
import { UiButtonModule } from '@cigna/shared/angular/button-ui';
import { HttpInterceptorWrapper } from './services/http.interceptor';
import { StepDirective } from './directives/step.directive';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule, TooltipComponent } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSortModule } from '@angular/material/sort';
import { MatDividerModule } from '@angular/material/divider';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommonChartsComponent } from './components/common-charts/common-charts.component';
import { CommonBarChartsComponent } from './components/common-bar-charts/common-bar-charts.component';
import { CommonHorizBarChartsComponent } from './components/common-horiz-bar-charts/common-horiz-bar-charts.component';
import {
  ComboChartComponent,
  ComboSeriesVerticalComponent,
} from './components/combo-chart';
import { MatAlertDialogComponent } from './components/mat-alert-dialog/mat-alert-dialog.component';
import { IdleDialogComponent } from './components/idle-dialog/idle-dialog.component';
import { CurrencyInputDirective } from './directives/currency-input.directive';
import { DatePickerFormatDirective } from './directives/date-picker-format.directive';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { SelectProducerCodeComponent } from './components/select-producer-code/select-producer-code.component';
import { LegalAffirmationModalComponent } from './components/legal-affirmation-modal/legal-affirmation-modal.component';
import { StatementAccessRequestComponent } from './components/statement-access-request/statement-access-request.component';

import { UiV3ModalModule } from '@cigna/shared/angular/ui-v3/modal-ui';
import { UiProgressModule } from '@cigna/shared/angular/progress-ui';
import { AnalyticsUiModule } from '@cigna/shared/angular/analytics-ui';
import { MatCustomDatepickerHeaderComponent } from './components/mat-custom-datepicker-header/mat-custom-datepicker-header.component';
import { InstantAlertComponent } from './components/instant-alert/instant-alert.component';
import { InstantAlertDialogComponent } from './components/instant-alert-dialog/instant-alert-dialog.component';
import { MfaTimeoutModalComponent } from './components/mfa-timeout-modal/mfa-timeout-modal.component';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { ProducerCommissionsContactUsDialogComponent } from './components/producer-commissions-contact-us-dialog/producer-commissions-contact-us-dialog.component';
import { TrimInputWhitespaceDirective } from './directives/trim-input-whitespace.directive';
import { ContractorAddressFormComponent } from './components/cigna-contract/contractor-address-form/contractor-address-form.component';
import { ContractWithCignaComponent } from './components/cigna-contract/contract-with-cigna/contract-with-cigna.component';
import { ContractorConfirmBoxComponent } from './components/cigna-contract/contractor-confirm-box/contractor-confirm-box.component';
import { CommercialContractorBoxComponent } from './components/cigna-contract/commercial-contractor-box/commercial-contractor-box.component';
import { BackCloseButtonComponent } from './components/cigna-contract/back-and-close-button/back-and-close-button.component';
import { ContractorExitDialogComponent } from './components/cigna-contract/contractor-exit-dialog/contractor-exit-dialog.component';
import { IfpContractorBoxComponent } from './components/cigna-contract/ifp-contractor-box/ifp-contractor-box.component';
import { MaContractorBoxComponent } from './components/cigna-contract/ma-contractor-box/ma-contractor-box.component';
import { CsbContractorBoxComponent } from './components/cigna-contract/csb-contractor-box/csb-contractor-box.component';
import { SubmissionFinalBoxComponent } from './components/cigna-contract/submission-final-box/submission-final-box.component';

const declarations = [
  StepDirective,
  CurrencyInputDirective,
  DatePickerFormatDirective,
  TrimInputWhitespaceDirective,
  MatAlertDialogComponent,
  IdleDialogComponent,
  CommonChartsComponent,
  CommonBarChartsComponent,
  CommonHorizBarChartsComponent,
  ComboChartComponent,
  ComboSeriesVerticalComponent,
  SelectProducerCodeComponent,
  LegalAffirmationModalComponent,
  StatementAccessRequestComponent,
  MatCustomDatepickerHeaderComponent,
  InstantAlertComponent,
  InstantAlertDialogComponent,
  MfaTimeoutModalComponent,
  ContactUsComponent,
  ProducerCommissionsContactUsDialogComponent,
  PageNotFoundComponent,
  ContractWithCignaComponent,
  ContractorAddressFormComponent,
  ContractorConfirmBoxComponent,
  CommercialContractorBoxComponent,
  BackCloseButtonComponent,
  ContractorExitDialogComponent,
  IfpContractorBoxComponent,
  MaContractorBoxComponent,
  CsbContractorBoxComponent,
  SubmissionFinalBoxComponent,
];

@NgModule({
  declarations,
  imports: [
    AnalyticsUiModule,
    UiProgressModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    RouterModule,
    UiIconModule,
    UiAlertModule,
    UiButtonModule,
    UiV3ModalModule,
    NgxMatSelectSearchModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatGridListModule,
    MatListModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatCardModule,
    MatTableModule,
    MatTabsModule,
    MatSortModule,
    MatDividerModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatChipsModule,
    MatPaginatorModule,
    FlexLayoutModule,
    MatExpansionModule,
    NgxChartsModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorWrapper,
      multi: true,
    },
  ],
  exports: [
    ...declarations,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatGridListModule,
    MatListModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatCardModule,
    MatTableModule,
    MatTabsModule,
    MatSortModule,
    MatDividerModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatChipsModule,
    MatPaginatorModule,
    UiAlertModule,
    MatExpansionModule,
    FlexLayoutModule,
    CurrencyInputDirective,
    NgxChartsModule,
  ],
})
export class CignaForBrokersSharedUiModule {}
